import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import SurveyInviteTable from '../../../components/tables/SurveyInviteTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useSurveyInviteSearch, useDeleteSurveyInvite } from '../../../hooks/useSurveyInviteApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { SurveyInviteDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const SurveyInviteList: React.FC = () => {
  const [formFilters, setFormFilters] = useState<any>({}); // Form input values
  const [searchFilters, setSearchFilters] = useState<any>({}); // Actual search criteria
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<SurveyInviteDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { search } = useSurveyInviteSearch();
  const deleteSurveyInvite = useDeleteSurveyInvite();

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Survey Invites', href: '#' },
  ];

  const totalPages = Math.ceil(totalRecords / pageSize);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await search({
        page: currentPage,
        pageSize,
        sortBy: 'CreatedUtc',
        sortDescending: true,
        filters: searchFilters,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching Survey Invites:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [currentPage, pageSize, searchFilters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchFilters(formFilters);
  };

  const handleClearSearch = () => {
    setFormFilters({});
    setSearchFilters({});
    setCurrentPage(1);
  };

  const handleDelete = async (dto: SurveyInviteDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteSurveyInvite.mutateAsync(dto.id || '');
      await fetchItems();
    } catch (error) {
      console.error('Error deleting SurveyInvite:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/surveyinvites"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission='Settings.SurveyInvite.View'
    >
      <Card
        title="Manage Survey Invites"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/surveyinvites/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                    <FormField
                            id="token"
                            label="Search by Token"
                            value={formFilters.token || ''}
                            onChange={(e) => setFormFilters({ ...formFilters, token: e.target.value })}
                            placeholder="Search by Token"
                        />
                        <FormField
                            id="label"
                            label="Search by Label"
                            value={formFilters.label || ''}
                            onChange={(e) => setFormFilters({ ...formFilters, label: e.target.value })}
                            placeholder="Search by Label"
                        />
                        <FormField
                            id="isSubmitted"
                            label="Search by Is Submitted"
                            value={formFilters.isSubmitted || ''}
                            onChange={(e) => setFormFilters({ ...formFilters, isSubmitted: e.target.value })}
                            placeholder="Search by Is Submitted"
                        />
                        <FormField
                            id="submittedUtc"
                            label="Search by Submitted Utc"
                            value={formFilters.submittedUtc || ''}
                            onChange={(e) => setFormFilters({ ...formFilters, submittedUtc: e.target.value })}
                            placeholder="Search by Submitted Utc"
                        />

          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <SurveyInviteTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/surveyinvites/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default SurveyInviteList;
